/**
 * Export utilities for worlds and adventures.
 * Downloads JSON files to the user's device.
 */
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logActivity } from "@/lib/activity-logger";
import { tFormat } from "@/lib/i18n/t-format";
import type { Translations } from "@/lib/i18n/types";

function downloadJSON(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9_\- ]/g, "").replace(/\s+/g, "_").substring(0, 60);
}

/** Fetch all rows from a table with pagination to bypass the 1000-row limit */
async function fetchAllRows(
  table: string,
  filters: { column: string; op: "eq" | "is" | "in"; value: any }[],
  orderBy: string = "created_at",
  selectColumns: string = "*",
): Promise<any[]> {
  const PAGE_SIZE = 1000;
  let allRows: any[] = [];
  let from = 0;

  while (true) {
    let query = supabase.from(table as any).select(selectColumns).order(orderBy).range(from, from + PAGE_SIZE - 1);
    for (const f of filters) {
      if (f.op === "eq") query = query.eq(f.column, f.value);
      else if (f.op === "is") query = query.is(f.column, f.value);
      else if (f.op === "in") query = query.in(f.column, f.value);
    }
    const { data, error } = await query;
    if (error) throw error;
    if (!data || data.length === 0) break;
    allRows = allRows.concat(data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return allRows;
}

/**
 * Export full world template: world metadata + entities + plots + agents + scenarios + media + documents + music + type defs
 */
export async function exportWorldTemplate(worldId: string, worldName: string, t: Translations) {
  // Confidential mode warning
  try {
    const { data: confCheck } = await supabase.from("worlds").select("is_confidential").eq("id", worldId).single();
    if (confCheck?.is_confidential === true) {
      const confirmed = window.confirm("This world is in Confidential Mode. Exported data may contain sensitive content. Continue?");
      if (!confirmed) return;
      logActivity({ action: "confidential_export", category: "world", details: { world_id: worldId, is_confidential: true } });
    }
  } catch {}

  const toastId = toast.loading(t.exp_loading_world);
  try {
    const [
      worldRes,
      entitiesRes,
      plotsRes,
      agentsRes,
      scenariosRes,
      mediaRes,
      docsRes,
      musicRes,
      typeDefsRes,
      checklistsRes,
    ] = await Promise.all([
      supabase.from("worlds").select("*").eq("id", worldId).single(),
      supabase.from("entities").select("*").eq("world_id", worldId).is("deleted_at", null).order("created_at"),
      supabase.from("plot_entries").select("*").eq("world_id", worldId).is("deleted_at", null).order("sort_order"),
      supabase.from("agent_definitions").select("*").eq("world_id", worldId).is("deleted_at", null).order("created_at"),
      supabase.from("scenarios").select("*").eq("world_id", worldId).is("deleted_at", null).order("sort_order"),
      supabase.from("world_media").select("*").eq("world_id", worldId).is("deleted_at", null).order("created_at"),
      supabase.from("world_documents").select("id, file_name, file_type, status, content_category, codex_summary, chunk_count, created_at").eq("world_id", worldId),
      supabase.from("music_tracks").select("*").eq("world_id", worldId).is("deleted_at", null).order("sort_order"),
      supabase.from("entity_type_definitions").select("*").eq("world_id", worldId),
      supabase.from("world_checklists").select("*").eq("world_id", worldId).order("sort_order"),
    ]);

    if (worldRes.error) throw worldRes.error;

    const exportData = {
      _export_version: 2,
      _exported_at: new Date().toISOString(),
      _type: "world_template",
      world: worldRes.data,
      entities: entitiesRes.data || [],
      plot_entries: plotsRes.data || [],
      agent_definitions: agentsRes.data || [],
      scenarios: scenariosRes.data || [],
      media: mediaRes.data || [],
      documents: docsRes.data || [],
      music_tracks: musicRes.data || [],
      entity_type_definitions: typeDefsRes.data || [],
      checklists: checklistsRes.data || [],
    };

    downloadJSON(exportData, `${sanitizeFilename(worldName)}_template.json`);
    toast.success(t.exp_world_success, { id: toastId });
  } catch (err: any) {
    toast.error(tFormat(t.exp_failed, { error: err.message }), { id: toastId });
  }
}

/**
 * Export player-visible adventure data: character, visible entities, visible plots, messages, quests, lore
 */
export async function exportPlayerData(adventureId: string, adventureName: string, t: Translations) {
  const toastId = toast.loading(t.exp_loading_player);
  try {
    const [advRes, entitiesRes, plotsRes, questsRes, memoriesRes, loreRes] = await Promise.all([
      supabase.from("adventures").select("id, name, created_at, updated_at, player_character, state, difficulty, is_multiplayer").eq("id", adventureId).single(),
      supabase.from("adventure_entities").select("*").eq("adventure_id", adventureId).is("deleted_at", null).in("visibility", ["visible"]),
      supabase.from("adventure_plots").select("*").eq("adventure_id", adventureId).is("deleted_at", null).in("visibility", ["visible"]),
      supabase.from("adventure_quests").select("*").eq("adventure_id", adventureId),
      supabase.from("memories").select("*").eq("adventure_id", adventureId).is("deleted_at", null).order("turn"),
      supabase.from("adventure_lore").select("*").eq("adventure_id", adventureId).order("discovered_turn"),
    ]);

    // Fetch all messages with pagination
    const messages = await fetchAllRows(
      "adventure_messages",
      [{ column: "adventure_id", op: "eq", value: adventureId }],
      "created_at",
    );

    if (advRes.error) throw advRes.error;

    const exportData = {
      _export_version: 2,
      _exported_at: new Date().toISOString(),
      _type: "player_export",
      adventure: advRes.data,
      messages,
      known_entities: entitiesRes.data || [],
      known_plots: plotsRes.data || [],
      quests: questsRes.data || [],
      memories: memoriesRes.data || [],
      lore: loreRes.data || [],
    };

    downloadJSON(exportData, `${sanitizeFilename(adventureName)}_player.json`);
    toast.success(t.exp_player_success, { id: toastId });
  } catch (err: any) {
    toast.error(tFormat(t.exp_failed, { error: err.message }), { id: toastId });
  }
}

/**
 * Export full game instance (admin only): all entities, plots, agents, messages, memories, players, lore
 */
export async function exportGameInstance(adventureId: string, adventureName: string, t: Translations) {
  const toastId = toast.loading(t.exp_loading_game);
  try {
    const [advRes, entitiesRes, plotsRes, agentsRes, playersRes, memoriesRes, questsRes, entityMemRes, loreRes] = await Promise.all([
      supabase.from("adventures").select("*").eq("id", adventureId).single(),
      supabase.from("adventure_entities").select("*").eq("adventure_id", adventureId).is("deleted_at", null),
      supabase.from("adventure_plots").select("*").eq("adventure_id", adventureId).is("deleted_at", null),
      supabase.from("adventure_agents").select("*").eq("adventure_id", adventureId).is("deleted_at", null),
      supabase.from("adventure_players").select("*").eq("adventure_id", adventureId),
      supabase.from("memories").select("*").eq("adventure_id", adventureId).is("deleted_at", null).order("turn"),
      supabase.from("adventure_quests").select("*").eq("adventure_id", adventureId),
      supabase.from("entity_memories").select("*").eq("adventure_id", adventureId).order("turn"),
      supabase.from("adventure_lore").select("*").eq("adventure_id", adventureId).order("discovered_turn"),
    ]);

    // Fetch all messages with pagination
    const messages = await fetchAllRows(
      "adventure_messages",
      [{ column: "adventure_id", op: "eq", value: adventureId }],
      "created_at",
    );

    if (advRes.error) throw advRes.error;

    const exportData = {
      _export_version: 2,
      _exported_at: new Date().toISOString(),
      _type: "game_instance_full",
      adventure: advRes.data,
      players: playersRes.data || [],
      messages,
      entities: entitiesRes.data || [],
      plots: plotsRes.data || [],
      agents: agentsRes.data || [],
      memories: memoriesRes.data || [],
      quests: questsRes.data || [],
      entity_memories: entityMemRes.data || [],
      lore: loreRes.data || [],
    };

    downloadJSON(exportData, `${sanitizeFilename(adventureName)}_full.json`);
    toast.success(t.exp_game_success, { id: toastId });
  } catch (err: any) {
    toast.error(tFormat(t.exp_failed, { error: err.message }), { id: toastId });
  }
}
